/**
 * Why a Vault upload was refused — in the reader's language, from the error's
 * CODE, never from its message. (Every Vault upload surface: the web Vault
 * page, the mobile capture screen.)
 *
 * The web page used to toast the server's `err.message` ("Files of type image/png
 * are not accepted in this section."), deliberately, because it said WHY — but
 * in English on every locale. The reason now travels as data
 * (`VAULT_CONTENT_REJECTED` + `data.reason`, `@tonnie/types`), and the upload
 * layer's own failures are typed (`UploadTooLargeError`, `UploadStalledError`),
 * so each gets copy that says what to do, and everything unrecognised gets the
 * page's generic line.
 */
import { apiErrorCode, DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { PITCH_MAX_DURATION_SECONDS } from '@tonnie/schemas';
import { isVaultContentRejectionReason, type VaultContentRejectionReason } from '@tonnie/types';

import { UploadStalledError, UploadTooLargeError } from '../client/upload';
import type { PricingTranslate } from './pricing-display';

export type VaultUploadFailure =
  | VaultContentRejectionReason
  | 'pitch_too_long'
  | 'file_too_large'
  | 'upload_stalled'
  | 'file_not_linkable'
  | 'upload_failed';

export function classifyVaultUploadFailure(err: unknown): VaultUploadFailure {
  if (err instanceof UploadStalledError) return 'upload_stalled';
  if (err instanceof UploadTooLargeError) return 'file_too_large';
  const code = apiErrorCode(err);
  if (code === DOMAIN_ERROR_CODES.CHECKUP_PITCH_TOO_LONG) return 'pitch_too_long';
  // A file picked from the Vault that is no longer there to link (D1).
  if (code === DOMAIN_ERROR_CODES.VAULT_FILE_NOT_LINKABLE) return 'file_not_linkable';
  if (code === DOMAIN_ERROR_CODES.VAULT_CONTENT_REJECTED) {
    const reason = (err as { data?: { reason?: unknown } }).data?.reason;
    if (isVaultContentRejectionReason(reason)) return reason;
  }
  return 'upload_failed';
}

/** A refusal about the file itself is the citizen's to fix, not an incident. */
export function isExpectedVaultUploadFailure(failure: VaultUploadFailure): boolean {
  return failure !== 'upload_failed';
}

/**
 * The copy — a switch over literal keys, so the key-usage gate checks each one.
 * `fallback` is the surface's own translated "upload failed", for what nobody
 * classified.
 */
export function vaultUploadFailureMessage(
  failure: VaultUploadFailure,
  t: PricingTranslate,
  fallback: string
): string {
  switch (failure) {
    case 'mime_not_allowed':
      return t('vault.uploadRejected.mimeNotAllowed');
    case 'unidentifiable':
      return t('vault.uploadRejected.unidentifiable');
    case 'declared_mime_mismatch':
      return t('vault.uploadRejected.declaredMimeMismatch');
    case 'pitch_too_long':
      return t('vault.uploadRejected.pitchTooLong', {
        minutes: PITCH_MAX_DURATION_SECONDS / 60,
      });
    case 'file_too_large':
      return t('vault.uploadRejected.fileTooLarge');
    case 'upload_stalled':
      return t('vault.uploadRejected.uploadStalled');
    case 'file_not_linkable':
      return t('vault.uploadRejected.fileNotLinkable');
    case 'upload_failed':
      return fallback;
    default: {
      const exhaustive: never = failure;
      return exhaustive;
    }
  }
}
